import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { apiClient } from '@/services/api-client';
import { websocketService } from '@/services/websocket-service';
import { tokenRefreshService } from '@/services/token-refresh.service';
import { useInactivityTimeout } from '@/hooks/useInactivityTimeout';

import { getErrorMessage } from '../utils/error.utils'
interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  permissions: string[];
  isActive: boolean;
  emailVerified: boolean;
  oabNumber?: string;
  oabState?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  register: (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  updateProfile: (updateData: {
    firstName?: string;
    lastName?: string;
    email?: string;
    oabNumber?: string;
    oabState?: string;
  }) => Promise<{ success: boolean; message?: string }>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const AUTH_METHOD_KEY = 'authMethod';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // Guarda tokens e liga os serviços que dependem da sessão.
  const startSession = (userData: User, tokens: { accessToken: string; refreshToken: string }) => {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`;
    setUser(userData);
    websocketService.connect(tokens.accessToken);
    tokenRefreshService.start();
  };

  // Quem já passou pela Cloudflare Access entra direto no próprio perfil.
  // Fora da Cloudflare (ou com o recurso desligado) a chamada falha e segue a tela de login.
  const loginWithCloudflareAccess = async (): Promise<boolean> => {
    try {
      const response = await apiClient.post('/api/auth/cloudflare');
      if (!response.data.success) {
        return false;
      }
      const { user: userData, tokens } = response.data.data;
      startSession(userData, tokens);
      localStorage.setItem(AUTH_METHOD_KEY, 'cloudflare');
      return true;
    } catch {
      return false;
    }
  };

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      if (accessToken) {
        try {
          // Set token in axios headers
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

          // Fetch user profile to validate token
          const response = await apiClient.get('/api/auth/profile');
          setUser(response.data.data.user ?? response.data.data);

          // Connect WebSocket with the restored token
          websocketService.connect(accessToken);

          // Start automatic token refresh service
          tokenRefreshService.start();
        } catch (error) {
          // Token might be expired, try refresh
          if (refreshToken) {
            const refreshed = await refreshTokenInternal(refreshToken);
            if (!refreshed) {
              clearAuthData();
              await loginWithCloudflareAccess();
            }
          } else {
            clearAuthData();
            await loginWithCloudflareAccess();
          }
        }
      } else {
        await loginWithCloudflareAccess();
      }

      setIsLoading(false);
    };

    initAuth();
    // Init-only effect: refreshTokenInternal é estável e roda apenas no mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for unauthorized events from API interceptor
  useEffect(() => {
    const handleUnauthorized = () => {
      // Clear auth data without calling API (already got 401)
      clearAuthData();
    };

    const handleTokenRefreshFailed = () => {
      // Token refresh service failed - logout user
      clearAuthData();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('auth:token-refresh-failed', handleTokenRefreshFailed);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('auth:token-refresh-failed', handleTokenRefreshFailed);
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await apiClient.post('/api/auth/login', {
        email,
        password
      });

      if (response.data.success) {
        const { user: userData, tokens } = response.data.data;
        
        // Store tokens
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        
        // Set auth header
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`;
        
        // Set user
        setUser(userData);

        // Connect WebSocket with the access token
        websocketService.connect(tokens.accessToken);

        // Start automatic token refresh service
        tokenRefreshService.start();

        return { success: true, message: 'Login successful' };
      } else {
        return { success: false, message: response.data.error?.message || 'Login failed' };
      }
    } catch (error: unknown) {
      console.error('Login error:', error);
      return {
        success: false,
        message: getErrorMessage(error, 'Login failed')
      };
    }
  };

  const register = async (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => {
    try {
      const response = await apiClient.post('/api/auth/register', userData);

      if (response.data.success) {
        const { user: newUser, tokens } = response.data.data;
        
        // Store tokens
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        
        // Set auth header
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`;
        
        // Set user
        setUser(newUser);

        // Connect WebSocket with the access token
        websocketService.connect(tokens.accessToken);

        // Start automatic token refresh service
        tokenRefreshService.start();

        return { success: true, message: 'Registration successful' };
      } else {
        return { success: false, message: response.data.error?.message || 'Registration failed' };
      }
    } catch (error: unknown) {
      console.error('Registration error:', error);
      return {
        success: false,
        message: getErrorMessage(error, 'Registration failed')
      };
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (refreshToken) {
        // Call logout endpoint
        await apiClient.post('/api/auth/logout', { refreshToken });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      const viaCloudflare = localStorage.getItem(AUTH_METHOD_KEY) === 'cloudflare';
      clearAuthData();
      if (viaCloudflare) {
        // Encerra também a sessão da Cloudflare; senão o próximo carregamento entraria de novo sozinho.
        window.location.href = '/cdn-cgi/access/logout';
      }
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    const refreshTokenValue = localStorage.getItem('refreshToken');
    if (!refreshTokenValue) {
      return false;
    }

    return refreshTokenInternal(refreshTokenValue);
  };

  const refreshTokenInternal = async (refreshTokenValue: string): Promise<boolean> => {
    try {
      const response = await apiClient.post('/api/auth/refresh', {
        refreshToken: refreshTokenValue
      });

      if (response.data.success) {
        const { tokens } = response.data.data;
        
        // Store new tokens
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        
        // Update auth header
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`;
        
        // Fetch updated user profile
        const profileResponse = await apiClient.get('/api/auth/profile');
        setUser(profileResponse.data.data.user);
        
        return true;
      } else {
        clearAuthData();
        return false;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      clearAuthData();
      return false;
    }
  };

  const clearAuthData = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem(AUTH_METHOD_KEY);
    delete apiClient.defaults.headers.common['Authorization'];
    setUser(null);

    // Disconnect WebSocket
    websocketService.disconnect();

    // Stop automatic token refresh service
    tokenRefreshService.stop();
  };

  const updateProfile = async (updateData: {
    firstName?: string;
    lastName?: string;
    email?: string;
    oabNumber?: string;
    oabState?: string;
  }) => {
    try {
      const response = await apiClient.put('/api/auth/profile', updateData);

      if (response.data.success) {
        const updatedUser = response.data.data.user;
        setUser(updatedUser);
        return { success: true, message: 'Perfil atualizado com sucesso' };
      } else {
        return { success: false, message: response.data.error?.message || 'Falha ao atualizar perfil' };
      }
    } catch (error: unknown) {
      console.error('Erro ao atualizar perfil:', error);
      return {
        success: false,
        message: getErrorMessage(error, 'Falha ao atualizar perfil')
      };
    }
  };

  // Inactivity timeout - auto-logout after 30 minutes of no activity
  const handleInactivityTimeout = useCallback(() => {
    console.warn('Session expired due to inactivity');
    clearAuthData();
    window.location.href = '/login?reason=inactivity';
  }, []);

  useInactivityTimeout({
    timeoutMinutes: 30,
    onTimeout: handleInactivityTimeout,
    enabled: isAuthenticated,
  });

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    refreshToken,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}